import { DEFAULT_REDIRECT_SECONDS, WRONG_PLATFORM_CODE } from '../constants/accountPlatform';
import { getArtistPlatformLoginUrl } from '../config/platformUrls';

type WrongPlatformPayload = {
  code?: string;
  message?: string;
  redirectUrl?: string;
};

const isWrongPlatformPayload = (value: unknown): value is WrongPlatformPayload =>
  Boolean(value && typeof value === 'object' && (value as WrongPlatformPayload).code === WRONG_PLATFORM_CODE);

export const parseWrongPlatformError = (errorOrPayload: unknown) => {
  const payload =
    (errorOrPayload as { response?: { data?: unknown } })?.response?.data || errorOrPayload;

  if (!isWrongPlatformPayload(payload)) {
    return null;
  }

  return {
    message:
      payload.message ||
      'This account is registered on the artist platform. Please log in there instead.',
    redirectUrl: getArtistPlatformLoginUrl(),
  };
};

const clearWrongPlatformSession = () => {
  localStorage.removeItem('userInfo');
  localStorage.removeItem('access_token');
};

const formatRedirectMessage = (
  baseMessage: string,
  redirectLabel: string,
  secondsRemaining: number
) => `${baseMessage} Redirecting you to ${redirectLabel} in ${secondsRemaining}s...`;

export const beginWrongPlatformRedirect = (
  payload: { message: string; redirectUrl: string },
  onTick: (message: string) => void,
  options?: { seconds?: number; redirectLabel?: string }
) => {
  clearWrongPlatformSession();

  const seconds = options?.seconds ?? DEFAULT_REDIRECT_SECONDS;
  const redirectLabel = options?.redirectLabel ?? 'artist login';
  let remaining = seconds;

  const tick = () => {
    onTick(formatRedirectMessage(payload.message, redirectLabel, remaining));
  };

  tick();

  const intervalId = window.setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      window.clearInterval(intervalId);
      window.location.replace(payload.redirectUrl);
      return;
    }
    tick();
  }, 1000);

  return () => window.clearInterval(intervalId);
};

export const handleWrongPlatformError = (
  errorOrPayload: unknown,
  onErrorMessage?: (message: string) => void,
  options?: { redirectLabel?: string; seconds?: number }
) => {
  const payload = parseWrongPlatformError(errorOrPayload);
  if (!payload) {
    return false;
  }

  beginWrongPlatformRedirect(payload, onErrorMessage || (() => {}), {
    redirectLabel: options?.redirectLabel ?? 'artist login',
    seconds: options?.seconds,
  });

  return true;
};
